import React from 'react';
import { CalendarClock, Pencil, Phone, StickyNote, User } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { useStudents } from '../../contexts/StudentsContext';
import { initialsOf, orDash, relativeTime } from '../../utils/format';
function DetailRow({
  label,
  value,
  icon: Icon
}: {
  label: string;
  value: string;
  icon?: typeof Phone;
}) {
  return <div className="rounded-2xl border border-[var(--glass-hairline)] bg-white/50 p-3 dark:bg-white/5" data-id="element-254" data-mp="y7swx8n0tom3">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-255" data-mp="1bzg1rwv145gh">
        {Icon && <Icon className="h-3 w-3" data-id="element-256" data-mp="176d8lpu5yy0t" />}
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-medium text-foreground" data-id="element-257" data-mp="jhbt5imwymm1">{orDash(value)}</p>
    </div>;
}
export function StudentDetailModal() {
  const {
    detail,
    closeDetail,
    openEditForm
  } = useStudents();
  return <Dialog open={Boolean(detail)} onOpenChange={open => open ? undefined : closeDetail()} data-id="element-258" data-mp="fhjx0erjwy38">
      <DialogContent className="glass-strong max-h-[92vh] max-w-xl overflow-y-auto scrollbar-slim rounded-3xl" data-id="element-259" data-mp="kflg2zoz8xep">
        {detail && <>
            <DialogHeader data-id="element-260" data-mp="u6pvg0ikeka2">
              <DialogTitle className="text-lg" data-id="element-261" data-mp="u5xxij1hwagm">Student record</DialogTitle>
              <DialogDescription data-id="element-262" data-mp="3sov3ynqa4rr0">Live row from the Supabase students table.</DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center gap-4 py-2 sm:flex-row sm:items-center" data-id="element-263" data-mp="1jd7blwra4en">
              <span aria-hidden="true" className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl brand-gradient text-lg font-semibold text-white" data-id="element-264" data-mp="6h57f37k0wtx">
                {detail.fullNames.trim() ? initialsOf(detail.fullNames) : '—'}
              </span>
              <div className="min-w-0 flex-1 text-center sm:text-left" data-id="element-265" data-mp="1lx42bcs5uwc8">
                <h2 className="truncate text-xl font-semibold tracking-tight text-foreground" data-id="element-266" data-mp="3iyjf0por5vjp">
                  {orDash(detail.fullNames)}
                </h2>
                <div className="mt-2 flex flex-wrap justify-center gap-2 sm:justify-start" data-id="element-267" data-mp="3dzf702dke3uy">
                  {detail.className.trim() && <Badge variant="secondary" className="rounded-full" data-id="element-268" data-mp="3mzv6l0r6squj">
                      {detail.className}
                    </Badge>}
                  {detail.gender && <Badge variant="outline" className="rounded-full" data-id="element-269" data-mp="2v9qpbayq4jxj">
                      {detail.gender}
                    </Badge>}
                </div>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground sm:justify-start" data-id="element-270" data-mp="349vlsq2dpxwx">
                  <CalendarClock className="h-3.5 w-3.5" data-id="element-271" data-mp="2gjsz6ph8hk5p" />
                  Added {relativeTime(detail.createdAtMs)}
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2" data-id="element-272" data-mp="r373gee275i3">
              <DetailRow label="Father's name" value={detail.fatherName} icon={User} data-id="element-273" data-mp="r1m9apvnpokr" />
              <DetailRow label="Father's telephone" value={detail.fatherPhone} icon={Phone} data-id="element-274" data-mp="vuxfcosna1bm" />
              <DetailRow label="Mother's name" value={detail.motherName} icon={User} data-id="element-275" data-mp="i3jzbkty3llc" />
              <DetailRow label="Mother's telephone" value={detail.motherPhone} icon={Phone} data-id="element-276" data-mp="n0slrxm4mbse" />
              <div className="sm:col-span-2" data-id="element-277" data-mp="1aqmzehe9s4dp">
                <DetailRow label="Small note" value={detail.note} icon={StickyNote} data-id="element-278" data-mp="1arxy42suylxy" />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end" data-id="element-279" data-mp="1eqf60ok3s0gf">
              <Button variant="ghost" className="h-10 rounded-xl" onClick={closeDetail} data-id="element-280" data-mp="10vctvjonxev6">
                Close
              </Button>
              <Button className="h-10 rounded-xl brand-gradient px-5 text-white hover:opacity-95" onClick={() => {
            const student = detail;
            closeDetail();
            openEditForm(student);
          }} data-id="element-281" data-mp="3lioe1oyllqgu">
                <Pencil className="mr-2 h-4 w-4" data-id="element-282" data-mp="4vbcplt2xl5i" />
                Edit record
              </Button>
            </div>
          </>}
      </DialogContent>
    </Dialog>;
}